import type {
  FieldConfig,
  FormInstance,
  FormValues,
  AddMoreConfig,
  CustomErrorMessages,
} from '../types';
import { mergeFormValues } from './valueMerger';

/**
 * Parses a date string in DD/MM/YYYY format to a Date object
 */
function parseDateString(dateStr: string): Date | null {
  if (!dateStr || typeof dateStr !== 'string') return null;

  // Handle DD/MM/YYYY format
  const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (match) {
    const [, day, month, year] = match;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    // Validate the date is valid
    if (
      date.getFullYear() === parseInt(year) &&
      date.getMonth() === parseInt(month) - 1 &&
      date.getDate() === parseInt(day)
    ) {
      return date;
    }
  }

  // Handle ISO format YYYY-MM-DD
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (
      date.getFullYear() === parseInt(year) &&
      date.getMonth() === parseInt(month) - 1 &&
      date.getDate() === parseInt(day)
    ) {
      return date;
    }
  }

  return null;
}

/**
 * Gets today's date with time set to 00:00:00 for accurate comparison
 */
function getToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/**
 * Parses a currency value, handling £, commas, and spaces
 */
function parseCurrency(value: unknown): number {
  if (value === undefined || value === null || value === '') return 0;
  const cleaned = String(value).replace(/[£,\s]/g, '');
  return parseFloat(cleaned) || 0;
}

/**
 * Resolves estate amounts for validation: same sources as the UI.
 * Uses mergeFormValues first so form-level fields match persisted state when in-memory
 * is empty on other steps. Completed estate-estimation-group wins for estate totals
 * (same priority as EstateProgressBar) so validation does not use stale localStorage
 * after the user updates those amounts on the estate step.
 */
function mergeEstateValues(
  values: FormValues,
  completedForms: Record<string, FormInstance[]>,
  localStorageKey?: string
): FormValues {
  const merged = {
    ...(mergeFormValues(values, completedForms, localStorageKey) as FormValues),
  };

  const estateEstimationInstances = completedForms['estate-estimation-group'] || [];
  if (estateEstimationInstances.length > 0) {
    const estateData = estateEstimationInstances[0].data;
    if (
      estateData.estimatedPropertyValue !== undefined &&
      estateData.estimatedPropertyValue !== null &&
      estateData.estimatedPropertyValue !== ''
    ) {
      merged.estimatedPropertyValue = estateData.estimatedPropertyValue;
    }
    if (
      estateData.estimatedCashEstateValue !== undefined &&
      estateData.estimatedCashEstateValue !== null &&
      estateData.estimatedCashEstateValue !== ''
    ) {
      merged.estimatedCashEstateValue = estateData.estimatedCashEstateValue;
    }
  }

  return merged;
}

/**
 * Calculates total allocated cash amounts from cash gift instances only
 * Used for cashGiftAmount and cashGiftToSomeoneAmount validation
 * Compares with only estimatedCashEstateValue
 */
function calculateTotalAllocatedCashGifts(
  completedForms: Record<string, FormInstance[]>,
  currentValue: number,
  currentFieldId: string,
  editingInstanceId?: string | null
): number {
  let totalAllocated = 0;

  // Determine which group the editing instance belongs to based on field ID
  const isCashGiftToCharity = currentFieldId === 'cashGiftAmount';
  const isCashGiftToSomeone = currentFieldId === 'cashGiftToSomeoneAmount';

  const actualEditingInstanceId = editingInstanceId;

  // Calculate from cash-gift-group (cash gifts to charity)
  // Only exclude editing instance if it belongs to this group
  const cashGiftInstances = (completedForms['cash-gift-group'] || []).filter((instance) => {
    return !(isCashGiftToCharity && instance.instanceId === actualEditingInstanceId);
  });
  cashGiftInstances.forEach((instance) => {
    const amount = parseCurrency(instance.data.cashGiftAmount);
    totalAllocated += amount;
  });

  // Calculate from cash-gift-to-someone-group (cash gifts to someone)
  // Only exclude editing instance if it belongs to this group
  const cashGiftToSomeoneInstances = (completedForms['cash-gift-to-someone-group'] || []).filter(
    (instance) => !(isCashGiftToSomeone && instance.instanceId === actualEditingInstanceId)
  );
  cashGiftToSomeoneInstances.forEach((instance) => {
    const amount = parseCurrency(instance.data.cashGiftToSomeoneAmount);
    totalAllocated += amount;
  });

  // Add current value (the new value being entered)
  if (isCashGiftToCharity || isCashGiftToSomeone) {
    totalAllocated += currentValue;
  }

  return totalAllocated;
}

/**
 * Calculates total allocated percentage from children instances
 * Used for childAmount and spousePartnerChildAmount validation
 * Compares percentage with total of estimatedCashEstateValue + estimatedPropertyValue
 */
function calculateTotalAllocatedChildrenPercentage(
  completedForms: Record<string, FormInstance[]>,
  currentValue: number,
  currentFieldId: string,
  editingInstanceId?: string | null
): number {
  let totalAllocatedPercentage = 0;

  // Calculate from children-group (child amounts)
  const childrenInstances = (completedForms['children-group'] || []).filter(
    (instance) => instance.instanceId !== editingInstanceId
  );
  childrenInstances.forEach((instance) => {
    const percentage = parseFloat(String(instance.data.childAmount || 0)) || 0;
    totalAllocatedPercentage += percentage;
  });

  // Calculate from spousePartnerChildrenFromPreviousRelationship-group (spouse partner child amounts)
  const spousePartnerChildrenInstances = (
    completedForms['spousePartnerChildrenFromPreviousRelationship-group'] || []
  ).filter((instance) => instance.instanceId !== editingInstanceId);
  spousePartnerChildrenInstances.forEach((instance) => {
    const percentage = parseFloat(String(instance.data.spousePartnerChildAmount || 0)) || 0;
    totalAllocatedPercentage += percentage;
  });

  const charityPercentageInstance = (completedForms['charities-group'] || []).filter(
    (instance) => instance.instanceId !== editingInstanceId
  );
  charityPercentageInstance.forEach((instance) => {
    const percentage = parseFloat(String(instance.data.charityPercentage || 0)) || 0;
    totalAllocatedPercentage += percentage;
  });

  // Add current value if it's a children percentage field
  if (
    currentFieldId === 'childAmount' ||
    currentFieldId === 'spousePartnerChildAmount' ||
    currentFieldId === 'charityPercentage'
  ) {
    const percentage =
      typeof currentValue === 'string' ? parseFloat(currentValue) || 0 : currentValue;
    totalAllocatedPercentage += percentage;
  }

  return totalAllocatedPercentage;
}

/**
 * Validates a single field based on its validation rules
 */
/** Helper to get string from Sitecore Field<string> (value may be at .value) */
function getFieldValue(field: CustomErrorMessages[keyof CustomErrorMessages]): string | undefined {
  if (!field || typeof field !== 'object') return undefined;
  const v = (field as { value?: string }).value;
  return typeof v === 'string' && v.trim() ? v : undefined;
}

export function validateField(
  field: FieldConfig,
  value: unknown,
  context?: {
    completedForms?: Record<string, FormInstance[]>;
    allValues?: FormValues;
    editingInstanceId?: string | null;
    addMoreConfig?: AddMoreConfig | undefined;
    localStorageKey?: string | undefined;
    validationInstanceId?: string | null;
    customErrorMessages?: CustomErrorMessages;
  }
): string | undefined {
  const rules = field.validation;
  const EMAIL_PATTERN = "^[A-Za-z0-9.!%&*_+'#?/\\-]+@[A-Za-z0-9\\-]+(\\.[A-Za-z0-9\\-]+)+$";
  const AMOUNT_PATTERN = '^(?:[1-9]\\d{0,6}(?:\\.\\d+)?|10000000(?:\\.0+)?)$';

  if (!rules) return undefined;

  const requiredRaw = (rules as { required?: boolean | string }).required;
  const isRequired =
    requiredRaw === true ||
    (typeof requiredRaw === 'string' && requiredRaw.trim().toLowerCase() === 'true');

  if (isRequired) {
    const isEmpty =
      value === undefined ||
      value === null ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === 'boolean' && value === false);

    if (isEmpty) {
      const message = (rules as { requiredValidationMessage?: string }).requiredValidationMessage;
      return typeof message === 'string' && message.trim() ? message : 'This field is required';
    }
  }

  // Skip further validation if field is empty and not required
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  // Date validation: Check if date is greater than today
  if (field.type === 'date' && typeof value === 'string') {
    const dateValue = parseDateString(value);
    if (dateValue) {
      const today = getToday();

      const minAllowed = new Date(1900, 0, 1); // 01/01/1900
      minAllowed.setHours(0, 0, 0, 0);
      if (dateValue < minAllowed) {
        return 'Date cannot be before 01/01/1900';
      }

      // Check if maxDate is set to "today" or if field has maxDate="today"
      const maxDate = field.maxDate;
      const shouldCheckMaxDate = maxDate === 'today' || maxDate === undefined;

      if (shouldCheckMaxDate && dateValue > today) {
        return 'Date cannot be in the future';
      }
    } else {
      return 'Invalid date';
    }
  }

  if (field.type === 'text' && typeof value === 'string' && rules.pattern === AMOUNT_PATTERN) {
    if (!new RegExp(AMOUNT_PATTERN).test(value)) {
      return 'Invalid amount: must be between 1 and 10000000';
    }
  }

  // Phone validation is handled by the phone input component itself
  // No additional number validation needed for phone fields

  if (typeof value === 'string') {
    const minLen = rules.min !== undefined ? Number(rules.min) : undefined;
    const maxLen = rules.max !== undefined ? Number(rules.max) : undefined;
    const rulesWithMessages = rules as {
      minValidationMessage?: string;
      maxValidationMessage?: string;
      patternMessage?: string;
    };
    // When min/max come from CMS, 0 means "no validation" for that rule
    if (minLen !== undefined && !isNaN(minLen) && minLen > 0 && value.length < minLen) {
      return typeof rulesWithMessages.minValidationMessage === 'string' &&
        rulesWithMessages.minValidationMessage.trim()
        ? rulesWithMessages.minValidationMessage
        : `Must be at least ${minLen} characters`;
    }
    if (maxLen !== undefined && !isNaN(maxLen) && maxLen > 0 && value.length > maxLen) {
      return typeof rulesWithMessages.maxValidationMessage === 'string' &&
        rulesWithMessages.maxValidationMessage.trim()
        ? rulesWithMessages.maxValidationMessage
        : `Must be at most ${maxLen} characters`;
    }
  }

  if (rules.pattern && typeof value === 'string') {
    const patternStr = typeof rules.pattern === 'string' ? rules.pattern : String(rules.pattern);
    const regex = new RegExp(patternStr);
    if (!regex.test(value)) {
      const patternMessage = (rules as { patternMessage?: string }).patternMessage;
      return typeof patternMessage === 'string' && patternMessage.trim()
        ? patternMessage
        : 'Invalid format';
    }
  }

  if (field.type === 'email' && typeof value === 'string') {
    const v = value.trim();

    const emailRegex = new RegExp(rules.pattern || EMAIL_PATTERN);
    if (!emailRegex.test(v)) {
      const patternMessage = (rules as { patternMessage?: string }).patternMessage;
      return typeof patternMessage === 'string' && patternMessage.trim()
        ? patternMessage
        : 'Enter a valid email address';
    }
  }

  const isTextOrPhoneWithPattern =
    (field.type === 'phone' || field.type === 'text') && rules.pattern;

  // Min/Max validation for numbers
  if ((rules.min !== undefined || rules.max !== undefined) && !isTextOrPhoneWithPattern) {
    const numValue = typeof value === 'string' ? Number(value) : Number(value);
    if (!isNaN(numValue)) {
      if (rules.min !== undefined && numValue < rules.min) {
        return `Value must be at least ${rules.min}`;
      }
      if (rules.max !== undefined && numValue > rules.max) {
        return `Value must be at most ${rules.max}`;
      }
    }
  }

  // Estate validation 1: Charity percentage cannot exceed 100% across all percentage allocations
  // Includes charities + children + spouse/partner children percentages.
  if (field.id === 'charityPercentage' && context?.completedForms) {
    const currentValue = typeof value === 'string' ? parseFloat(value) || 0 : Number(value) || 0;
    const totalAllocated = calculateTotalAllocatedChildrenPercentage(
      context.completedForms,
      currentValue,
      field.id,
      context.editingInstanceId || context.validationInstanceId
    );

    if (totalAllocated > 100) {
      const customMsg =
        context.customErrorMessages &&
        getFieldValue(context.customErrorMessages.charityPercentageExceedMessage) &&
        getFieldValue(context.customErrorMessages.charityPercentageExceedMessage)?.replace(
          '{{charityPercentage}}',
          totalAllocated.toFixed(1) + '%'
        );
      return (
        customMsg ?? `Total charity percentage (${totalAllocated.toFixed(1)}%) would exceed 100%`
      );
    }
  }

  // Estate validation 2: Cash amounts (cashGiftAmount, cashGiftToSomeoneAmount)
  // Compare with only estimatedCashEstateValue
  if (
    (field.id === 'cashGiftAmount' || field.id === 'cashGiftToSomeoneAmount') &&
    context?.completedForms &&
    context?.allValues
  ) {
    const estimatedCashEstateValue = parseCurrency(
      mergeEstateValues(context.allValues, context.completedForms, context.localStorageKey)
        .estimatedCashEstateValue
    );

    const currentValue = parseCurrency(value);
    const totalAllocated = calculateTotalAllocatedCashGifts(
      context.completedForms,
      currentValue,
      field.id,
      context.editingInstanceId || context.validationInstanceId
    );

    if (totalAllocated > estimatedCashEstateValue) {
      const customMsg =
        field.id === 'cashGiftAmount'
          ? context.customErrorMessages &&
            getFieldValue(context.customErrorMessages.charityCashAmountExceedMessage)
              ?.replace('{{charityCashAmount}}', `£${totalAllocated.toLocaleString()}`)
              ?.replace('{{estimatedCashAmount}}', `£${estimatedCashEstateValue.toLocaleString()}`)
          : context.customErrorMessages &&
            getFieldValue(context.customErrorMessages.beneficiaryCashAmountExceedMessage)
              ?.replace('{{beneficiaryCashAmount}}', `£${totalAllocated.toLocaleString()}`)
              ?.replace('{{estimatedCashAmount}}', `£${estimatedCashEstateValue.toLocaleString()}`);
      if (customMsg) return customMsg;
      const fieldName =
        field.id === 'cashGiftAmount' ? 'cash gift to charity' : 'cash gift to someone';
      return `Total ${fieldName} allocations (£${totalAllocated.toLocaleString()}) would exceed your estimated cash estate value (£${estimatedCashEstateValue.toLocaleString()})`;
    }
  }

  // Estate validation 3: Children percentages (childAmount, spousePartnerChildAmount)
  // Compare percentage with total of estimatedCashEstateValue + estimatedPropertyValue
  if (
    (field.id === 'childAmount' || field.id === 'spousePartnerChildAmount') &&
    context?.completedForms &&
    context?.allValues
  ) {
    // Note: Percentage validation compares against total of estimatedCashEstateValue + estimatedPropertyValue
    // but since percentages are capped at 100%, we only need to check if total percentage exceeds 100%
    const currentPercentage =
      typeof value === 'string' ? parseFloat(value) || 0 : Number(value) || 0;
    const totalAllocatedPercentage = calculateTotalAllocatedChildrenPercentage(
      context.completedForms,
      currentPercentage,
      field.id,
      context.editingInstanceId || context.validationInstanceId
    );

    if (totalAllocatedPercentage > 100) {
      const customMsg =
        context.customErrorMessages &&
        getFieldValue(context.customErrorMessages.childrenPercentageExceedMessage)?.replace(
          '{{childrenPercenatge}}',
          totalAllocatedPercentage.toFixed(1) + '%'
        );
      if (customMsg) return customMsg;
      const fieldName =
        field.id === 'childAmount'
          ? 'Childrens allocated percentage'
          : 'Spouse/Partner children allocated percentage';
      return `Total ${fieldName} (${totalAllocatedPercentage.toFixed(1)}%) would exceed 100%`;
    }
  }

  return undefined;
}

export function scrollToFirstError(errors: Record<string, string | undefined>): void {
  const errorFieldIds = Object.keys(errors).filter((fieldId) => errors[fieldId]);
  if (errorFieldIds.length === 0) return;

  const firstErrorFieldId = errorFieldIds[0];

  // Try multiple selectors to find the field element
  const selectors = [
    `input[name="${firstErrorFieldId}"]`,
    `textarea[name="${firstErrorFieldId}"]`,
    `select[name="${firstErrorFieldId}"]`,
    `input[id="${firstErrorFieldId}"]`,
    `textarea[id="${firstErrorFieldId}"]`,
    `select[id="${firstErrorFieldId}"]`,
    `[data-field-id="${firstErrorFieldId}"]`,
  ];

  // Use requestAnimationFrame to ensure DOM is updated after errors are set
  requestAnimationFrame(() => {
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus the element if it's an input
        if (
          element instanceof HTMLInputElement ||
          element instanceof HTMLTextAreaElement ||
          element instanceof HTMLSelectElement
        ) {
          element.focus();
        }
        return;
      }
    }

    // Fallback: try to find by field wrapper or label
    const fieldWrapper = document.querySelector(`[data-field-wrapper="${firstErrorFieldId}"]`);
    if (fieldWrapper) {
      fieldWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
}
